import { PayloadAction } from '@reduxjs/toolkit';
import { FetchError } from '@yandex-360/telemost-sdk';
import type { CloudAPI } from '@yandex-360/telemost-sdk/cloud-api';
import { call, put, select, take, takeEvery, takeLatest } from 'typed-redux-saga';

import { selectIsTeleMessenger } from '../../../../shared/features/config/selectors';
import {
    selectIsCalendarSummarizationFeatureEnabled,
    selectIsOneTimeElementsShownAlways,
} from '../../../../shared/features/flags/selectors';
import { sendCloudApiCommand } from '../../../../shared/features/moderation/sagas';
import {
    hideNotification,
    NOTIFICATIONS_MAP,
    notify,
} from '../../../../shared/features/notifications/ps-notifications';
import { parentFrameCommunicator } from '../../../../shared/features/parent-frame-communicator';
import {
    hideSummarizationStartPopupAction,
    hideSummarizationWelcomePopupAction,
    selectSummarizationStartPopupWasShown,
    selectSummarizationWelcomePopupWasShown,
} from '../../../../shared/features/settings';
import { createLogger } from '../../../../shared/helpers/loggerFacade';
import { countMetrika } from '../../../../shared/helpers/metrika';
import { cloudApi } from '../../../../shared/helpers/transport';
import { SummarizationMailRecipient, SummarizationStatus } from '../../../../types';
import { ConferenceStatus } from '../../constants';
import { OutgoingEvents } from '../../iframe-sagas/types';
import { getConferenceConfig, selectIsPermissionsExtendedEnabled } from '../../selectors';
import { NotificationId } from '../../types';
import { setConferenceStatus } from '../conference/slice';
import { selectHasSummarizationSettingsPermission } from '../permissions';
import { selectIsSummarizationAvailable, selectIsSummarizationMailForParticipants } from './selectors';
import {
    setIsSummarizationMailForParticipants,
    setIsSummarizationMailForParticipantsAction,
    setIsSummarizationModalClosedAction,
    setSummarizationModalId,
    setSummarizationStatus,
    showSummarizationMailRecipientToastAction,
    showSummarizationNoPermissionToStartToastAction,
    startSummarizationAction,
    stopSummarizationAction,
} from './slice';
import { SummarizationModalId } from './types';

const logger = createLogger('summarization');

type SummarizationCommand = Extract<CloudAPI.SendCommand.ApiCommand, 'start-summarization' | 'stop-summarization'>;

function* countSummarizationMetrica(command: string) {
    yield* call(countMetrika, 'meeting_screen', 'summarization', command);
}

function* sendSummarizationCommandSaga(
    command: SummarizationCommand,
    params: Pick<CloudAPI.SendCommand.Request, 'mailRecipient'> = {}
) {
    try {
        yield* call(sendCloudApiCommand, command, params);
        yield* call(countSummarizationMetrica, command);
    } catch (error) {
        logger.error(error, { method: 'sendSummarizationCommandSaga', additional: { command } });

        const isPermissionsExtendedEnabled = yield* select(selectIsPermissionsExtendedEnabled);
        const errorNotification = (() => {
            if (command === 'start-summarization') {
                return NOTIFICATIONS_MAP[NotificationId.SUMMARIZATION_START_FAILED]();
            }

            if (isPermissionsExtendedEnabled && FetchError.is(error) && error.status === 403) {
                return NOTIFICATIONS_MAP[NotificationId.SUMMARIZATION_NO_PERMISSION_TO_STOP]();
            }

            return NOTIFICATIONS_MAP[NotificationId.SUMMARIZATION_STOP_FAILED]();
        })();

        yield* put(notify(errorNotification));
    }
}

function* showSummarizationModal(modalId: SummarizationModalId) {
    yield* put(setSummarizationModalId(modalId));

    const { payload } = yield* take(setIsSummarizationModalClosedAction);

    yield* put(setSummarizationModalId(null));

    return payload;
}

function* startSummarizationSaga() {
    const summarizationStartPopupWasShown = yield* select(selectSummarizationStartPopupWasShown);
    const isOneTimeElementsShownAlways = yield* select(selectIsOneTimeElementsShownAlways);
    const isCalendarSummarizationFeatureEnabled = yield* select(selectIsCalendarSummarizationFeatureEnabled);

    const currentIsSummarizationMailForParticipants = yield* select(selectIsSummarizationMailForParticipants);
    let isSummarizationMailForParticipants: Maybe<boolean>;
    if ((!summarizationStartPopupWasShown || isOneTimeElementsShownAlways) && !isCalendarSummarizationFeatureEnabled) {
        const { isConfirmed, isSummarizationMailForParticipants: desiredIsSummarizationMailForParticipants } =
            yield* call(showSummarizationModal, 'START');
        isSummarizationMailForParticipants = desiredIsSummarizationMailForParticipants;

        if (!isOneTimeElementsShownAlways) {
            yield* put(hideSummarizationStartPopupAction());
        }

        if (!isConfirmed) {
            return;
        }
    }

    const mailRecipient =
        (isSummarizationMailForParticipants ?? currentIsSummarizationMailForParticipants)
            ? SummarizationMailRecipient.PARTICIPANTS_FROM_ORGANIZATION
            : SummarizationMailRecipient.ADMINS_FROM_ORGANIZATION;
    const hasSummarizationSettingsPermission = yield* select(selectHasSummarizationSettingsPermission);
    const params = hasSummarizationSettingsPermission ? { mailRecipient } : undefined;
    yield* call(sendSummarizationCommandSaga, 'start-summarization', params);
    if (
        isSummarizationMailForParticipants !== undefined &&
        currentIsSummarizationMailForParticipants !== isSummarizationMailForParticipants
    ) {
        yield* call(
            countMetrika,
            'meeting_screen',
            'summarization',
            'summarization_mail_for_participants_checkbox_on_start',
            isSummarizationMailForParticipants ? 'allow' : 'deny'
        );
        yield* call(updateSummarizationMailRecipient, isSummarizationMailForParticipants);
    }
}

function* stopSummarizationSaga() {
    const { isConfirmed, isSummarizationMailForParticipants } = yield* call(showSummarizationModal, 'STOP');

    if (!isConfirmed) {
        return;
    }

    const currentIsSummarizationMailForParticipants = yield* select(selectIsSummarizationMailForParticipants);
    const mailRecipient =
        (isSummarizationMailForParticipants ?? currentIsSummarizationMailForParticipants)
            ? SummarizationMailRecipient.PARTICIPANTS_FROM_ORGANIZATION
            : SummarizationMailRecipient.ADMINS_FROM_ORGANIZATION;
    const hasSummarizationSettingsPermission = yield* select(selectHasSummarizationSettingsPermission);
    const params = hasSummarizationSettingsPermission ? { mailRecipient } : undefined;
    yield* call(sendSummarizationCommandSaga, 'stop-summarization', params);
    if (
        isSummarizationMailForParticipants !== undefined &&
        currentIsSummarizationMailForParticipants !== isSummarizationMailForParticipants
    ) {
        yield* call(
            countMetrika,
            'meeting_screen',
            'summarization',
            'summarization_mail_for_participants_checkbox_on_stop',
            isSummarizationMailForParticipants ? 'allow' : 'deny'
        );
        yield call(updateSummarizationMailRecipient, isSummarizationMailForParticipants);
    }
}

function* hideSummarizationNotifications() {
    yield* put(hideNotification(NotificationId.SUMMARIZATION_START));
    yield* put(hideNotification(NotificationId.SUMMARIZATION_START_FAILED));
    yield* put(hideNotification(NotificationId.SUMMARIZATION_STOP));
    yield* put(hideNotification(NotificationId.SUMMARIZATION_STOP_FAILED));
}

function* showSummarizationWelcomeModal({ payload: conferenceStatus }: { payload: ConferenceStatus }) {
    if (conferenceStatus === ConferenceStatus.JOINED) {
        const isSummarizationAvailable = yield* select(selectIsSummarizationAvailable);
        const summarizationWelcomePopupWasShown = yield* select(selectSummarizationWelcomePopupWasShown);
        const isOneTimeElementsShownAlways = yield* select(selectIsOneTimeElementsShownAlways);

        if ((isSummarizationAvailable && !summarizationWelcomePopupWasShown) || isOneTimeElementsShownAlways) {
            yield* call(showSummarizationModal, 'WELCOME');

            if (!isOneTimeElementsShownAlways) {
                yield* put(hideSummarizationWelcomePopupAction());
            }
        }
    }
}

function* setIsSummarizationMailForParticipantsSaga({
    payload: isSummarizationMailForParticipants,
}: PayloadAction<boolean>) {
    const currentIsSummarizationMailForParticipants = yield* select(selectIsSummarizationMailForParticipants);
    if (currentIsSummarizationMailForParticipants === null) {
        return;
    }

    const config = yield* select(getConferenceConfig);
    const uri = config?.resolved_uri;
    if (!uri) {
        return;
    }

    const mailRecipient = isSummarizationMailForParticipants
        ? SummarizationMailRecipient.PARTICIPANTS_FROM_ORGANIZATION
        : SummarizationMailRecipient.ADMINS_FROM_ORGANIZATION;

    try {
        yield* put(setIsSummarizationMailForParticipants(isSummarizationMailForParticipants));
        yield* call([cloudApi, cloudApi.setSummarizationSettings], uri, {
            mail_recipient: mailRecipient,
        });
        yield* call(updateSummarizationMailRecipient, isSummarizationMailForParticipants);
    } catch (error) {
        const isTeleMessenger = yield* select(selectIsTeleMessenger);
        if (isTeleMessenger) {
            yield* call([parentFrameCommunicator, parentFrameCommunicator.sendMessage], '*', {
                event: OutgoingEvents.SET_SUMMARIZATION_MAIL_FOR_PARTICIPANTS,
                payload: {
                    value: currentIsSummarizationMailForParticipants,
                },
            });
        }
        yield* put(setIsSummarizationMailForParticipants(currentIsSummarizationMailForParticipants));
        const isCalendarSummarizationFeatureEnabled = yield* select(selectIsCalendarSummarizationFeatureEnabled);
        if (isCalendarSummarizationFeatureEnabled) {
            yield* put(notify(NOTIFICATIONS_MAP[NotificationId.PERMISSIONS_ERROR]()));
        } else {
            yield* put(
                notify(
                    NOTIFICATIONS_MAP[NotificationId.SUMMARIZATION_MAIL_RECIPIENT_UPDATE_ERROR](
                        currentIsSummarizationMailForParticipants
                    )
                )
            );
        }

        yield* call(countMetrika, 'messages', 'msg_summarization_mail_for_participants_fail');

        logger.error(error, {
            method: 'setIsSummarizationMailForParticipantsSaga',
            additional: {
                mailRecipient,
            },
        });
    }
}

export function* updateSummarizationStatus(summarizationStatus: SummarizationStatus) {
    yield* put(setSummarizationStatus(summarizationStatus));

    yield* hideSummarizationNotifications();

    switch (summarizationStatus) {
        case 'RUNNING': {
            const isSummarizationMailForParticipants = yield* select(selectIsSummarizationMailForParticipants);
            const isCalendarSummarizationFeatureEnabled = yield* select(selectIsCalendarSummarizationFeatureEnabled);
            if (isSummarizationMailForParticipants !== null) {
                yield* put(
                    notify(
                        NOTIFICATIONS_MAP[NotificationId.SUMMARIZATION_START](
                            isSummarizationMailForParticipants,
                            isCalendarSummarizationFeatureEnabled
                        )
                    )
                );
            }
            break;
        }
        case 'TURNED_OFF':
            yield* put(notify(NOTIFICATIONS_MAP[NotificationId.SUMMARIZATION_STOP]()));
            break;
    }
}

export function* updateSummarizationMailRecipient(isSummarizationMailForParticipants: boolean) {
    const currentIsSummarizationMailForParticipants = yield* select(selectIsSummarizationMailForParticipants);
    const isTeleMessenger = yield* select(selectIsTeleMessenger);

    yield* put(setIsSummarizationMailForParticipants(isSummarizationMailForParticipants));
    if (isTeleMessenger) {
        yield* call([parentFrameCommunicator, parentFrameCommunicator.sendMessage], '*', {
            event: OutgoingEvents.SET_SUMMARIZATION_MAIL_FOR_PARTICIPANTS,
            payload: {
                value: isSummarizationMailForParticipants,
            },
        });
    }
    const isCalendarSummarizationFeatureEnabled = yield* select(selectIsCalendarSummarizationFeatureEnabled);
    if (currentIsSummarizationMailForParticipants !== null && !isCalendarSummarizationFeatureEnabled) {
        yield* call(showMailRecipientToast, isSummarizationMailForParticipants);
    }
}

function* showMailRecipientToast(isSummarizationMailForParticipants: boolean) {
    const isCalendarSummarizationFeatureEnabled = yield* select(selectIsCalendarSummarizationFeatureEnabled);
    if (isCalendarSummarizationFeatureEnabled) {
        return;
    }
    const notification = NOTIFICATIONS_MAP[NotificationId.SUMMARIZATION_MAIL_RECIPIENT](
        isSummarizationMailForParticipants
    );
    yield* call(countMetrika, 'messages', 'msg_summarization_mail_for_participants_success');
    yield* put(notify(notification));
}

function* showMailRecipientToastSaga() {
    const isSummarizationMailForParticipants = yield* select(selectIsSummarizationMailForParticipants);
    if (isSummarizationMailForParticipants !== null) {
        yield* call(showMailRecipientToast, isSummarizationMailForParticipants);
    }
}

function* showSummarizationNoPermissionToStartToastSaga() {
    const isCalendarSummarizationFeatureEnabled = yield* select(selectIsCalendarSummarizationFeatureEnabled);
    if (isCalendarSummarizationFeatureEnabled) {
        return;
    }
    yield* put(notify(NOTIFICATIONS_MAP[NotificationId.SUMMARIZATION_NO_PERMISSION_TO_START]()));
}

export function* summarizationSagas() {
    yield* takeEvery(startSummarizationAction, startSummarizationSaga);
    yield* takeEvery(stopSummarizationAction, stopSummarizationSaga);

    yield* takeEvery(setConferenceStatus, showSummarizationWelcomeModal);
    yield* takeLatest(setIsSummarizationMailForParticipantsAction, setIsSummarizationMailForParticipantsSaga);
    yield* takeEvery(showSummarizationMailRecipientToastAction, showMailRecipientToastSaga);
    yield* takeEvery(showSummarizationNoPermissionToStartToastAction, showSummarizationNoPermissionToStartToastSaga);
}
